// =============================================================================
// DataSheet View Spec — the single canonical representation of a table's
// client-side projection (filter + sort + highlight).
//
// One spec, many producers: the manual Excel column controls and (Phase 2) the
// AI table agent both emit into the SAME ViewSpec, and ONE engine (evaluateView)
// applies it. Pagination layers on top of the engine's output. Adding new logic
// never means extending the DataSheet — it's all expressed in this grammar and
// run by the generic evaluator below.
// =============================================================================

/** Primitive a column resolves to (via DataSheetColumn.valueAccessor, else row[key]). */
export type CellValue = string | number | boolean | null;

export type ViewOperator =
    | 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte'
    | 'contains' | 'notContains' | 'isEmpty' | 'isNotEmpty';

/** A reference to a column's value, or a literal constant. */
export type Operand = { col: string } | { value: string | number };

/**
 * A boolean expression over column values. Leaf = comparison (column vs constant
 * OR column vs column). Compound = and/or/not. This is the whole vocabulary —
 * anything a producer (manual UI or agent) can express already evaluates.
 */
export type ViewExpr =
    | { kind: 'cmp'; left: { col: string }; op: ViewOperator; right: Operand }
    | { kind: 'and'; exprs: ViewExpr[] }
    | { kind: 'or'; exprs: ViewExpr[] }
    | { kind: 'not'; expr: ViewExpr };

/** Sort by a column, or by a predicate (true-first when desc) — e.g. "highlighted on top". */
export interface SortClause {
    by: { col: string } | { expr: ViewExpr };
    direction: 'asc' | 'desc';
    nullsLast?: boolean;
}

export interface HighlightRule {
    when: ViewExpr;
    style: 'amber' | 'red' | 'green' | 'blue';
    label?: string;
}

/** The canonical projection state. Empty = the as-is API view (identity). */
export interface ViewSpec {
    filters: ViewExpr[]; // AND-combined
    sorts: SortClause[]; // applied in order (multi-key, stable)
    highlights: HighlightRule[];
}

export const EMPTY_VIEW_SPEC: ViewSpec = { filters: [], sorts: [], highlights: [] };

/** Resolver: given a row and a column key, returns the value the engine compares on. */
export type CellResolver<T> = (row: T, colKey: string) => CellValue;

// ─── Expression evaluation ─────────────────────────────────────────────────

function resolveOperand<T>(operand: Operand, row: T, resolve: CellResolver<T>): CellValue {
    return 'col' in operand ? resolve(row, operand.col) : operand.value;
}

function isBlank(v: CellValue): boolean {
    return v === null || v === undefined || v === '';
}

function evalCmp<T>(expr: Extract<ViewExpr, { kind: 'cmp' }>, row: T, resolve: CellResolver<T>): boolean {
    const a = resolve(row, expr.left.col);
    if (expr.op === 'isEmpty') return isBlank(a);
    if (expr.op === 'isNotEmpty') return !isBlank(a);

    const b = resolveOperand(expr.right, row, resolve);
    const aNum = Number(a);
    const bNum = Number(b);
    const numeric = a !== null && a !== '' && b !== null && b !== '' && !Number.isNaN(aNum) && !Number.isNaN(bNum);

    switch (expr.op) {
        case 'eq':  return numeric ? aNum === bNum : String(a ?? '') === String(b ?? '');
        case 'neq': return numeric ? aNum !== bNum : String(a ?? '') !== String(b ?? '');
        case 'gt':  return numeric && aNum > bNum;
        case 'gte': return numeric && aNum >= bNum;
        case 'lt':  return numeric && aNum < bNum;
        case 'lte': return numeric && aNum <= bNum;
        case 'contains':    return String(a ?? '').toLowerCase().includes(String(b ?? '').toLowerCase());
        case 'notContains': return !String(a ?? '').toLowerCase().includes(String(b ?? '').toLowerCase());
        default: return true;
    }
}

/** Evaluate a boolean expression for one row. Deterministic; no side effects. */
export function evalExpr<T>(expr: ViewExpr, row: T, resolve: CellResolver<T>): boolean {
    switch (expr.kind) {
        case 'cmp': return evalCmp(expr, row, resolve);
        case 'and': return expr.exprs.every(e => evalExpr(e, row, resolve));
        case 'or':  return expr.exprs.some(e => evalExpr(e, row, resolve));
        case 'not': return !evalExpr(expr.expr, row, resolve);
        default: return true;
    }
}

// ─── The engine ──────────────────────────────────────────────────────────────

function compareValues(a: CellValue, b: CellValue, nullsLast: boolean): number {
    const aBlank = isBlank(a);
    const bBlank = isBlank(b);
    if (aBlank && bBlank) return 0;
    if (aBlank) return nullsLast ? 1 : -1;
    if (bBlank) return nullsLast ? -1 : 1;
    const aNum = Number(a);
    const bNum = Number(b);
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum === bNum ? 0 : aNum < bNum ? -1 : 1;
    const aStr = String(a);
    const bStr = String(b);
    return aStr === bStr ? 0 : aStr < bStr ? -1 : 1;
}

function sortKeyValue<T>(clause: SortClause, row: T, resolve: CellResolver<T>): CellValue {
    return 'col' in clause.by
        ? resolve(row, clause.by.col)
        : (evalExpr(clause.by.expr, row, resolve) ? 1 : 0);
}

/**
 * The single generic projector. Applies a ViewSpec to rows and returns the
 * filtered + sorted rows plus a per-row highlight style map. Pure and
 * deterministic — the FE's only view-logic engine; producers never compute rows.
 *
 * @param skipSort  When the table sorts server-side (controlled sort), the rows
 *                  are already ordered — apply filters/highlights only.
 */
export function evaluateView<T>(
    rows: T[],
    resolve: CellResolver<T>,
    spec: ViewSpec,
    getRowKey: (row: T, index: number) => string,
    options: { skipSort?: boolean } = {},
): { rows: T[]; highlightByKey: Map<string, HighlightRule['style']> } {
    let out = rows;

    if (spec.filters.length > 0) {
        out = out.filter(row => spec.filters.every(f => evalExpr(f, row, resolve)));
    }

    if (!options.skipSort && spec.sorts.length > 0) {
        // Stable multi-key sort: decorate with original index, compare clause-by-clause.
        out = out
            .map((row, index) => ({ row, index }))
            .sort((x, y) => {
                for (const clause of spec.sorts) {
                    const cmp = compareValues(
                        sortKeyValue(clause, x.row, resolve),
                        sortKeyValue(clause, y.row, resolve),
                        clause.nullsLast ?? true,
                    );
                    if (cmp !== 0) return clause.direction === 'asc' ? cmp : -cmp;
                }
                return x.index - y.index;
            })
            .map(d => d.row);
    }

    const highlightByKey = new Map<string, HighlightRule['style']>();
    if (spec.highlights.length > 0) {
        out.forEach((row, index) => {
            const key = getRowKey(row, index);
            for (const rule of spec.highlights) {
                if (evalExpr(rule.when, row, resolve)) { highlightByKey.set(key, rule.style); break; }
            }
        });
    }

    return { rows: out, highlightByKey };
}

// ─── Manual-UI ⇄ ViewSpec adapters ───────────────────────────────────────────
// The Excel column controls are just another ViewSpec producer. A manual column
// filter is a top-level `cmp` (column vs constant); manual sort is a single
// column SortClause. These helpers let the header read/write those without
// touching agent-authored compound expressions.

/** The single-column manual filter for a column, if one exists (ignores compound/agent exprs). */
export function getColumnFilter(spec: ViewSpec, colKey: string):
    { op: ViewOperator; value: string } | null {
    const f = spec.filters.find(
        (e): e is Extract<ViewExpr, { kind: 'cmp' }> =>
            e.kind === 'cmp' && e.left.col === colKey && 'value' in e.right,
    );
    if (!f || !('value' in f.right)) return null;
    return { op: f.op, value: String(f.right.value) };
}

/** Set / clear the single-column manual filter for a column. */
export function setColumnFilter(
    spec: ViewSpec,
    colKey: string,
    filter: { op: ViewOperator; value: string } | null,
): ViewSpec {
    const others = spec.filters.filter(
        e => !(e.kind === 'cmp' && e.left.col === colKey && 'value' in e.right),
    );
    if (!filter) return { ...spec, filters: others };
    const needsValue = filter.op !== 'isEmpty' && filter.op !== 'isNotEmpty';
    const cmp: ViewExpr = {
        kind: 'cmp',
        left: { col: colKey },
        op: filter.op,
        right: { value: needsValue ? filter.value : '' },
    };
    return { ...spec, filters: [...others, cmp] };
}

/** The manual (single-column) sort direction for a column, if it's the active sort. */
export function getColumnSort(spec: ViewSpec, colKey: string): 'asc' | 'desc' | null {
    const s = spec.sorts.find(c => 'col' in c.by && c.by.col === colKey);
    return s ? s.direction : null;
}

/** Replace the sort with a single column clause, or clear it (direction null). */
export function setColumnSort(spec: ViewSpec, colKey: string, direction: 'asc' | 'desc' | null): ViewSpec {
    if (!direction) return { ...spec, sorts: [] };
    return { ...spec, sorts: [{ by: { col: colKey }, direction }] };
}
